import React, { useState } from 'react';
import Layout from 'layouts/manager/Layout';
import Pagination from 'components/Pagination';
import Table, { TableColumn } from 'components/Table';

const tableData = [
  {
    memberNo: '0001',
    storeName: '東京本店',
    address: '東京都千代田区1-1-1',
    company: '株式会社A',
    agencyName: '代理店A',
    startedAt: '2025-01-01',
    renewedAt: '2025-06-01',
    usageCount: 12,
    annualUsageCount: 5,
    contractPlan: 'ベーシック',
    status: 'active',
  },
  {
    memberNo: '0002',
    storeName: '大阪支店',
    address: '大阪府大阪市北区2-2-2',
    company: '株式会社B',
    agencyName: '代理店B',
    startedAt: '2025-02-01',
    renewedAt: '2025-06-10',
    usageCount: 8,
    annualUsageCount: 3,
    contractPlan: 'プレミアム',
    status: 'inactive',
  },
  {
    memberNo: '0003',
    storeName: '名古屋営業所',
    address: '愛知県名古屋市中区3-3-3',
    company: '株式会社C',
    agencyName: '代理店C',
    startedAt: '2025-03-01',
    renewedAt: '2025-06-20',
    usageCount: 15,
    annualUsageCount: 7,
    contractPlan: 'カスタム',
    status: 'active',
  },
];

type TableRow = {
  memberNo: string;
  storeName: string;
  address: string;
  company: string;
  agencyName: string;
  startedAt: string;
  renewedAt: string;
  usageCount: number;
  annualUsageCount: number;
  contractPlan: string;
  status: string;
};

const actionLinks = [
  { label: '編集', color: 'text-green-700', hover: 'hover:underline', url: (row: TableRow) => `/manager/contract/edit/${row.memberNo}` },
  { label: '削除', color: 'text-red-600', hover: 'hover:underline', url: (row: TableRow) => `/manager/contract/delete/${row.memberNo}` },
];

type SortKey = keyof TableRow;
type SortOrder = 'asc' | 'desc';

const columns: TableColumn<TableRow>[] = [
  { key: 'memberNo', label: '会員番号' },
  { key: 'storeName', label: '訪問先店舗名', render: (row) => <a href={`/manager/member/edit/${row.memberNo}`} className="text-blue-700 underline hover:text-blue-900">{row.storeName}</a> },
  { key: 'address', label: '訪問先住所' },
  { key: 'company', label: '会社名' },
  { key: 'agencyName', label: '代理店名' },
  { key: 'startedAt', label: '開始日' },
  { key: 'renewedAt', label: '契約更新日' },
  { key: 'usageCount', label: '合計回数' },
  { key: 'annualUsageCount', label: '年間回数' },
  { key: 'contractPlan', label: '契約内容' },
  { key: 'status', label: 'ステータス', render: (row) => row.status === 'active' ? '有効' : '無効' },
];

const Search: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState<number[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('memberNo');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [search, setSearch] = useState({
    appliedAt: '',
    startedAt: '',
    renewedAt: '',
    memberNo: '',
    storeName: '',
    company: '',
    usageCount: '',
    annualUsageCount: '',
    contractPlan: '',
    paymentMethod: '',
    contractNo: '',
    agencyNo: '',
    agencyName: '',
    status: '',
  });
  // ページネーション用
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pagedData = [...tableData].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  }).slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // チェックボックス
  const handleCheck = (idx: number) => {
    setChecked((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  const handleCheckAll = () => {
    if (checked.length === tableData.length) {
      setChecked([]);
    } else {
      setChecked(tableData.map((_, idx) => idx));
    }
  };

  // ソートリンク
  const renderSortLink = (key: SortKey, label: string) => (
    <button
      type="button"
      className="flex items-center text-xs font-medium text-gray-700 hover:underline focus:outline-none"
      onClick={() => {
        if (sortKey === key) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortKey(key);
          setSortOrder('asc');
        }
      }}
    >
      {label}
      {sortKey === key && (
        <span className="ml-1 text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  );

  return (
    <Layout>
      <div className="mx-auto">
        {/* タイトル */}
        <h1 className="mb-3 text-lg font-bold text-gray-800">契約情報管理</h1>
        {/* 検索項目 */}
        <div className="mb-6">
          <button
            className="flex items-center w-full px-4 py-2 rounded-t-lg"
            style={{ backgroundColor: '#f7fafd' }}
            onClick={() => setOpen((v) => !v)}
            type="button"
          >
            <span
              className="mr-2 text-lg transition-transform"
              style={{ color: '#002c6a', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            >
              ▼
            </span>
            <span className="flex-1 text-base font-bold text-left" style={{ color: '#002c6a' }}>
              検索
            </span>
          </button>
          {open && (
            <div className="p-4 bg-white border rounded-b-lg shadow">
              <form className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">申込日</label>
                  <input type="date" className="w-full px-2 py-1 border rounded" value={search.appliedAt} onChange={e => setSearch({ ...search, appliedAt: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">開始日</label>
                  <input type="date" className="w-full px-2 py-1 border rounded" value={search.startedAt} onChange={e => setSearch({ ...search, startedAt: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">契約更新日</label>
                  <input type="date" className="w-full px-2 py-1 border rounded" value={search.renewedAt} onChange={e => setSearch({ ...search, renewedAt: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">会員番号</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.memberNo} onChange={e => setSearch({ ...search, memberNo: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">訪問先店舗名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.storeName} onChange={e => setSearch({ ...search, storeName: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">会社名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.company} onChange={e => setSearch({ ...search, company: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">合計利用回数</label>
                  <input type="number" className="w-full px-2 py-1 border rounded" value={search.usageCount} onChange={e => setSearch({ ...search, usageCount: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">年間利用回数</label>
                  <input type="number" className="w-full px-2 py-1 border rounded" value={search.annualUsageCount} onChange={e => setSearch({ ...search, annualUsageCount: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">契約内容</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.contractPlan} onChange={e => setSearch({ ...search, contractPlan: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">お支払い方法</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.paymentMethod} onChange={e => setSearch({ ...search, paymentMethod: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">法人契約No</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.contractNo} onChange={e => setSearch({ ...search, contractNo: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">代理店No</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.agencyNo} onChange={e => setSearch({ ...search, agencyNo: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">代理店名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.agencyName} onChange={e => setSearch({ ...search, agencyName: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">ステータス</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.status} onChange={e => setSearch({ ...search, status: e.target.value })}>
                    <option value="">全て</option>
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                  </select>
                </div>
                <div className="flex justify-start mt-2 md:col-span-3">
                  <button
                    type="submit"
                    className="px-3 py-1 text-sm transition rounded"
                    style={{ color: '#ffffff', background: '#002c6a' }}
                  >
                    検索
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
        {/* 一覧テーブル */}
        <div className="p-4 bg-white border rounded shadow">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex gap-2">
              <button
                className="px-3 py-1 text-xs font-semibold border border-blue-900 rounded"
                style={{ color: '#002c6a', background: '#f7fafd' }}
                type="button"
              >
                CSVエクスポート
              </button>
            </div>
          </div>
          <Table
            data={pagedData}
            columns={columns}
            checked={checked}
            onCheck={handleCheck}
            onCheckAll={handleCheckAll}
            showCheckbox
            actions={actionLinks.map(link => ({
              ...link,
              href: link.url,
            }))}
            renderSortLink={renderSortLink}
            actionColWidthClass="w-24"
          />
          {/* ページネーション */}
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>
    </Layout>
  );
};

export default Search;
